import React, { useState } from 'react';
import {
  Card,
  Form,
  InputNumber,
  Select,
  Button,
  DatePicker,
  Space,
  Typography,
  message,
  TimePicker,
  Divider,
} from 'antd';
import {
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { config } from '../config/env';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from '../contexts/LocationContext';
import dayjs, { Dayjs } from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

interface EnergyDataEntry {
  timestamp: string;
  solarOutput: number;
  windOutput: number;
  batteryLevel: number;
  gridStatus: 'importing' | 'exporting' | 'neutral';
}

export const ManualDataEntry: React.FC = () => {
  const { user } = useAuth();
  const { lat, lng } = useLocation();
  const [form] = Form.useForm();
  const [entries, setEntries] = useState<EnergyDataEntry[]>([]);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (values: any) => {
    if (!user?.id) {
      message.error('Please log in to save data');
      return;
    }

    try {
      setSaving(true);

      // Format timestamp
      const date = values.date || dayjs();
      const time = values.time || dayjs();
      const timestamp = date
        .hour(time.hour())
        .minute(time.minute())
        .second(time.second())
        .toISOString();

      const entry: EnergyDataEntry = {
        timestamp,
        solarOutput: values.solarOutput || 0,
        windOutput: values.windOutput || 0,
        batteryLevel: values.batteryLevel || 0,
        gridStatus: values.gridStatus || 'neutral',
      };

      // Save to backend
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${config.apiUrl}/api/data/energy`,
        [entry],
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      message.success('Data saved successfully');
      form.resetFields();
      
      // Add to entries list
      setEntries([entry, ...entries].slice(0, 10)); // Keep last 10 entries
    } catch (error: any) {
      message.error(`Failed to save data: ${error.response?.data?.error || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleBulkSubmit = async () => {
    if (entries.length === 0) {
      message.warning('No entries to save');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${config.apiUrl}/api/data/energy`,
        entries,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      message.success(`${entries.length} entries saved successfully`);
      setEntries([]);
    } catch (error: any) {
      message.error(`Failed to save entries: ${error.response?.data?.error || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
      <Title level={2}>Manual Data Entry</Title>
      <Text type="secondary">
        Enter energy data manually (solar output, wind output, battery level, grid status)
      </Text>

      <Card style={{ marginTop: '24px' }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            date: dayjs(),
            time: dayjs(),
            gridStatus: 'neutral',
          }}
        >
          <Form.Item
            label="Date"
            name="date"
            rules={[{ required: true, message: 'Please select a date' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="Time"
            name="time"
            rules={[{ required: true, message: 'Please select a time' }]}
          >
            <TimePicker style={{ width: '100%' }} format="HH:mm:ss" showNow />
          </Form.Item>

          <Form.Item
            label="Solar Output (kW)"
            name="solarOutput"
            rules={[
              { required: true, message: 'Please enter solar output' },
              { type: 'number', min: 0, max: 10000 },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="0.00"
              step={0.1}
              min={0}
              max={10000}
            />
          </Form.Item>

          <Form.Item
            label="Wind Output (kW)"
            name="windOutput"
            rules={[
              { required: true, message: 'Please enter wind output' },
              { type: 'number', min: 0, max: 5000 },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="0.00"
              step={0.1}
              min={0}
              max={5000}
            />
          </Form.Item>

          <Form.Item
            label="Battery Level (%)"
            name="batteryLevel"
            rules={[
              { required: true, message: 'Please enter battery level' },
              { type: 'number', min: 0, max: 100 },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="0"
              step={1}
              min={0}
              max={100}
            />
          </Form.Item>

          <Form.Item
            label="Grid Status"
            name="gridStatus"
            rules={[{ required: true, message: 'Please select grid status' }]}
          >
            <Select>
              <Option value="importing">Importing</Option>
              <Option value="exporting">Exporting</Option>
              <Option value="neutral">Neutral</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
              >
                Save Entry
              </Button>
              <Button
                onClick={() => {
                  const values = form.getFieldsValue();
                  if (values.date && values.time) {
                    const date = values.date || dayjs();
                    const time = values.time || dayjs();
                    const timestamp = date
                      .hour(time.hour())
                      .minute(time.minute())
                      .second(time.second())
                      .toISOString();

                    const entry: EnergyDataEntry = {
                      timestamp,
                      solarOutput: values.solarOutput || 0,
                      windOutput: values.windOutput || 0,
                      batteryLevel: values.batteryLevel || 0,
                      gridStatus: values.gridStatus || 'neutral',
                    };

                    setEntries([...entries, entry]);
                    form.resetFields();
                    message.success('Entry added to batch');
                  }
                }}
                icon={<PlusOutlined />}
              >
                Add to Batch
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {entries.length > 0 && (
        <Card style={{ marginTop: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Title level={4}>Batch Entries ({entries.length})</Title>
            <Space>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => setEntries([])}
              >
                Clear All
              </Button>
              <Button
                type="primary"
                onClick={handleBulkSubmit}
                loading={saving}
              >
                Save All ({entries.length})
              </Button>
            </Space>
          </div>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {entries.map((entry, idx) => (
              <Card
                key={idx}
                size="small"
                style={{ marginBottom: '8px' }}
              >
                <Space>
                  <Text>{new Date(entry.timestamp).toLocaleString()}</Text>
                  <Text>Solar: {entry.solarOutput} kW</Text>
                  <Text>Wind: {entry.windOutput} kW</Text>
                  <Text>Battery: {entry.batteryLevel}%</Text>
                  <Text>Grid: {entry.gridStatus}</Text>
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => setEntries(entries.filter((_, i) => i !== idx))}
                  />
                </Space>
              </Card>
            ))}
          </div>
        </Card>
      )}

      <Card style={{ marginTop: '24px' }}>
        <Title level={4}>Location</Title>
        <Text>
          Data will be saved with your current location: {lat.toFixed(4)}, {lng.toFixed(4)}
        </Text>
      </Card>
    </div>
  );
};

export default ManualDataEntry;
